// ** React Imports
import { useContext, useEffect, useMemo, useRef, useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import useResizeObserver from "@react-hook/resize-observer";

// ** Custom Components
import Block from "components/layout/Block";
import Dropdown from "components/layout/Dropdown";
import { Icon } from "components/icons";
import Text from "components/typography/Text";

// ** Selector Components
import SearchInput from "./components/SearchInput";
import PreferencesMenuItemsMulti from "./components/PreferencesMenuItemsMulti";
import Activator from "./components/Activator";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";
import { NoResultItem } from "./components/NoResultItem";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useSelected from "hooks/useSelected";

// ** Utils & Helpers
import slug from "utils/slug";
import { preferencesgroupedByCategory } from "./components/preferencesGroupedByCategory";
import { borderRadiuses } from "styles/declares";
import styled from "styled-components";
import { overflowChanger } from "helpers/overflowChanger";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const MAX_VISIBLE_SELECTED_ITEMS = 3;

const StyledSelectorLabel = styled.span`
	display: flex;
	justify-content: space-between;
	padding: 0.5rem 1rem 0;
	padding-top: 6px; // Inaczej dropdown nie układa się równo z krawędzią aktywatora.
	margin-bottom: 0.75rem;
	font-size: 0.8rem;
	line-height: 12px;
	border-top: 1px solid transparent;
	cursor: pointer;
	height: 23px;
	user-select: none;
`;

// #####################################################

export const PreferencesSelectorParentMulti = ({
	label,
	items,
	selected,
	onAccept,
	activatorStyleOptions,
	isBan,
	customMobileLayerStyle,
	isKeyboardOpen,
	visualViewportHeight,
}) => {
	const { t } = useTranslation(["common"]);
	const { isPhone } = useContext(deviceTypeContext);
	const language = useSelector((state) => state.globalCommon.language);
	const { isOpen, close, open } = useOpenClose();

	const activatorRef = useRef(null);
	const wrapperRef = useRef(null);

	const [searchValue, setSearchValue] = useState("");
	const [layerWidth, setLayerWidth] = useState(
		(activatorStyleOptions?.noPad
			? wrapperRef?.current?.clientWidth
			: wrapperRef?.current?.clientWidth - 7) *
			(activatorStyleOptions?.scaleLayer || 1),
	);

	const selectedLimitText = t("userpanel:data.cantSaveMorePreferences");
	const customSelectedLimitText = selectedLimitText?.replace(/10/g, "20");

	const finalItems = useMemo(
		() =>
			items.map((item, index) => ({
				...item,
				key: label
					? `${slug(label)}-item-${item.key || index}`
					: `item-${item.key || index}`,
			})),
		[items, language],
	);

	const extendedClose = ({ insideUseEffect = false } = {}) => {
		if (insideUseEffect) {
			close();
		} else {
			close(() => {
				overflowChanger({ conditionName: "ifIsHidden" });
			});
		}
	};

	// ** Dropdown opcje konfiguracyjne
	const useLayerOptionsMemo = useMemo(
		() => ({
			overflowContainer: false,
			placement: "bottom-start",
			possiblePlacements: ["bottom-start", "top-start"],
			// -1 bo inaczej dropdown nie układa się równo z krawędzią aktywatora.
			triggerOffset: activatorRef?.current
				? -(activatorRef.current?.offsetHeight - 1)
				: 0,
		}),
		[isOpen],
	);
	const useLayerOptionsFinal = {
		onOutsideClick: () => {
			close(() => {
				overflowChanger({ conditionName: "ifIsHidden" });
				onAccept(checkedItemsArray);
			});
		},
		onParentClose: () => {
			extendedClose();
		},
		...useLayerOptionsMemo,
	};

	const maxLayerHeight = 310;
	const customLayerStyleFinal = {
		minHeight: activatorRef?.current
			? `${activatorRef.current?.offsetHeight}px`
			: 0,
		zIndex: isPhone ? 1000 : 499,
		borderRadius: `${borderRadiuses.card}rem`,
		borderTopRightRadius: `${borderRadiuses.card}rem`,
		borderTopLeftRadius: `${borderRadiuses.card}rem`,
		boxShadow: "var(--dz-ref-shadow-input-bottom)",
	};

	const {
		selected: checkedItemsArray,
		check: checkItem,
		hasSelected,
	} = useSelected(
		items.map((item) => item.value),
		selected,
	);
	const currentSelectedItems = useMemo(
		() =>
			finalItems.filter((item) => checkedItemsArray.includes(item.value)),
		[finalItems, checkedItemsArray],
	);

	const isSelected = Boolean(checkedItemsArray.length);

	const andMoreNormal =
		currentSelectedItems.length > MAX_VISIBLE_SELECTED_ITEMS
			? currentSelectedItems.length - MAX_VISIBLE_SELECTED_ITEMS
			: false;

	const groupedByCategory = currentSelectedItems.reduce((acc, item) => {
		if (!acc[item.category]) {
			acc[item.category] = [];
		}
		acc[item.category].push(item);
		return acc;
	}, {});

	const useMore = false;
	const renderPreferencesGroupedByCategory = () => {
		return preferencesgroupedByCategory({
			groupedByCategory,
			currentSelectedItems,
			t,
		});
	};
	const displayText = isSelected
		? useMore && andMoreNormal
			? `${t("common:selected")}\u00A0(${checkedItemsArray.length})`
			: renderPreferencesGroupedByCategory
		: t("common:choose");
	const lastClosedDisplayText = useRef(displayText);

	useResizeObserver(wrapperRef, (entry) => {
		setLayerWidth(
			(activatorStyleOptions?.noPad
				? entry.contentRect.width
				: entry.contentRect.width - 7) *
				(activatorStyleOptions?.scaleLayer || 1),
		);
	});

	// ** Aktualizuj referencję tylko gdy dropdown jest zamknięty, inaczej jak zmieniają się kategorie w Aktywatorze to zawartość Dropdowna nie jest równo z jego górną krawędzią.
	if (!isOpen) {
		lastClosedDisplayText.current = displayText;
	}

	// ** Użyj zamrożonej wartości gdy dropdown jest otwarty
	const finalDisplayText = isOpen
		? lastClosedDisplayText.current
		: displayText;

	const searchSlug = useMemo(() => {
		return searchValue.length > 0
			? slug(searchValue.toLowerCase(), " ")
			: "";
	}, [searchValue]);

	const filteredItems = useMemo(() => {
		if (!searchSlug) return items;

		return items.filter((item) => {
			const itemSlug = slug(item.content, " ").toLowerCase();
			return itemSlug.includes(searchSlug);
		});
	}, [items, searchSlug]);

	useEffect(() => {
		if (!isOpen && searchValue.length > 0) {
			setSearchValue("");
		}
	}, [isOpen]);

	const searchInputRef = useRef(null);

	// #####################################################

	return (
		<Block
			ref={wrapperRef}
			style={{
				flex: activatorStyleOptions?.flex1 ? "1" : "unset",
				width: isPhone ? "100%" : "unset",
			}}
		>
			<Dropdown
				isOpen={isOpen}
				close={(insideUseEffect) => {
					extendedClose(insideUseEffect);
				}}
				useLayerOptions={useLayerOptionsFinal}
				customLayerStyle={customLayerStyleFinal}
				layerWidth={layerWidth}
				withMobileVersion={true}
				mobileTitle={label}
				customMobileLayerStyle={customMobileLayerStyle}
				isKeyboardOpen={isKeyboardOpen}
				visualViewportHeight={visualViewportHeight}
				isSingleDropdown={true}
				isMultiDropdown={true} // is signle but in this selector we want less height
				autoBorder={false}
				noAnimation={true}
				menuContent={() =>
					isOpen && (
						<>
							{!isPhone && (
								<StyledSelectorLabel
									onClick={() => {
										onAccept(checkedItemsArray);
										extendedClose();
									}}
								>
									{label}
									<Text
										size="1rem"
										style={{
											marginTop: "10px",
										}}
									>
										<Icon
											name={
												isOpen
													? "caret-up"
													: "caret-down"
											}
										/>
									</Text>
								</StyledSelectorLabel>
							)}

							<Block
								full
								width="100%"
								style={{
									height: "100%",
									maxHeight: isPhone
										? "unset"
										: `${maxLayerHeight}px`,
								}}
							>
								<Block
									full
									style={{
										overflow: "auto",
										height: "100%",
										maxHeight: isPhone
											? "unset"
											: `${maxLayerHeight - 10}px`,
									}}
								>
									<SearchInput
										searchInputRef={searchInputRef}
										searchValue={searchValue}
										setSearchValue={setSearchValue}
										isOpen={isOpen}
										customPadding={
											isPhone
												? "1rem 1rem 0 1rem"
												: "0 1rem 0 1rem"
										}
									/>
									{filteredItems.length > 0 ? (
										<PreferencesMenuItemsMulti
											searchValue={searchValue}
											handleSelectItem={(item) => {
												checkItem(item.value);
											}}
											checkedItemsArray={
												checkedItemsArray
											}
										/>
									) : (
										<NoResultItem
											onClick={() => {
												setSearchValue("");
											}}
											searchInputRef={searchInputRef}
											isOpen={isOpen}
										/>
									)}
								</Block>
							</Block>
						</>
					)
				}
			>
				<DropdownTriggerContext.Consumer>
					{({ triggerProps }) => (
						<Activator
							activatorOptions={{
								...activatorStyleOptions,
								isMultiple: true,
							}}
							activatorStyle={{
								alignItems: "flex-start",
								borderRadius: `var(--dz-sys-input-border-radius)`,
								opacity: isOpen
									? 0
									: isBan
										? "var(--dz-sys-button-disabled-opacity)"
										: 1,
							}}
							customCaretMargin="6px"
							innerRef={(node) => {
								triggerProps.ref(node);
								activatorRef.current = node;
							}}
							label={label}
							onClick={() => {
								if (isPhone) {
									overflowChanger({
										conditionName: "ifIsNotHidden",
									});
								}
								open();
							}}
							isOpen={isOpen}
							isPhone={isPhone}
							displayContent={
								isPhone ? displayText : finalDisplayText
							}
							isSelected={isSelected}
							hasSelected={hasSelected}
							selectedLimitText={customSelectedLimitText}
							selectedLimit={20}
							disabled={isBan}
							layerSide="bottom" // Nie usuwa labela i nie skacze zawartość aktywatora gdy dropdown otwiera się od góry (a i tak zasłania ją dropdown).
						/>
					)}
				</DropdownTriggerContext.Consumer>
			</Dropdown>
		</Block>
	);
};
